import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { X, Check, Trophy, FlagCheckered, SoccerBall, Baseball, Basketball, Football } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS_OPTIONS = [
  { key: "nfl", name: "NFL", icon: "football" },
  { key: "nba", name: "NBA", icon: "basketball" },
  { key: "mlb", name: "MLB", icon: "baseball" },
  { key: "nhl", name: "NHL", icon: "hockey" },
  { key: "epl", name: "Premier League", icon: "soccer" },
  { key: "mls", name: "MLS", icon: "soccer" },
  { key: "pga", name: "PGA Golf", icon: "golf" },
  { key: "nascar", name: "NASCAR", icon: "racing" },
  { key: "ufc", name: "UFC", icon: "mma" },
  { key: "ncaaf", name: "NCAA Football", icon: "football" },
  { key: "ncaab", name: "NCAA Basketball", icon: "basketball" },
];

export default function PreferencesModal({ isOpen, onClose }) {
  const { user, refreshUser, authHeaders } = useAuth();
  const [favoriteSports, setFavoriteSports] = useState([]);
  const [favoriteTeams, setFavoriteTeams] = useState([]);
  const [allTeams, setAllTeams] = useState({});
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeLeague, setActiveLeague] = useState("nba");

  // Load preferences
  useEffect(() => {
    if (!isOpen || !user) return;
    const prefs = user.preferences || {};
    setFavoriteSports(prefs.favorite_sports || []);
    setFavoriteTeams(prefs.favorite_teams || []);
  }, [isOpen, user]);

  // Load teams for selected league
  useEffect(() => {
    if (!isOpen || !activeLeague || allTeams[activeLeague]) return;
    setLoadingTeams(true);
    axios.get(`${API}/teams/${activeLeague}`).then((r) => {
      setAllTeams((prev) => ({ ...prev, [activeLeague]: r.data.teams || [] }));
    }).catch(() => {}).finally(() => setLoadingTeams(false));
  }, [isOpen, activeLeague, allTeams]);

  const toggleSport = (key) => {
    setFavoriteSports((prev) => prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]);
  };

  const toggleTeam = (team) => {
    setFavoriteTeams((prev) => {
      const exists = prev.some((t) => t.id === team.id && t.league === activeLeague);
      if (exists) return prev.filter((t) => !(t.id === team.id && t.league === activeLeague));
      return [...prev, { id: team.id, name: team.displayName || team.name, abbreviation: team.abbreviation, logo: team.logo, league: activeLeague }];
    });
  };

  const isTeamFavorite = (teamId) => favoriteTeams.some((t) => t.id === teamId && t.league === activeLeague);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/preferences`, { favorite_teams: favoriteTeams, favorite_sports: favoriteSports }, { headers: authHeaders(), withCredentials: true });
      await refreshUser();
      onClose();
    } catch (err) {
      console.error("Failed to save preferences:", err);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const currentTeams = allTeams[activeLeague] || [];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" data-testid="preferences-modal">
      <div className="bg-[#141414] border border-white/10 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="font-['Bebas_Neue'] text-2xl tracking-wider uppercase text-white">Your Preferences</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="close-preferences">
            <X size={18} className="text-white/60" />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(85vh-130px)] p-5 space-y-6">
          {/* Favorite Sports */}
          <div>
            <h3 className="text-xs tracking-widest uppercase font-bold text-white/40 mb-3">Favorite Sports</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="sports-selection">
              {SPORTS_OPTIONS.map((sport) => (
                <button
                  key={sport.key}
                  onClick={() => toggleSport(sport.key)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    favoriteSports.includes(sport.key)
                      ? "bg-[#007AFF]/20 border-[#007AFF]/50 text-[#007AFF]"
                      : "bg-white/5 border-white/10 text-white/60 hover:border-white/20"
                  }`}
                  data-testid={`sport-toggle-${sport.key}`}
                >
                  {favoriteSports.includes(sport.key) && <Check size={14} weight="bold" />}
                  {sport.name}
                </button>
              ))}
            </div>
          </div>

          {/* Favorite Teams */}
          <div>
            <h3 className="text-xs tracking-widest uppercase font-bold text-white/40 mb-3">Favorite Teams</h3>
            {/* League tabs for team selection */}
            <div className="flex gap-1 overflow-x-auto mb-3 pb-1">
              {SPORTS_OPTIONS.filter((s) => !["pga", "nascar"].includes(s.key)).map((sport) => (
                <button
                  key={sport.key}
                  onClick={() => setActiveLeague(sport.key)}
                  className={`px-2.5 py-1 rounded text-[10px] tracking-wider uppercase font-semibold whitespace-nowrap transition-colors ${
                    activeLeague === sport.key ? "bg-[#007AFF] text-white" : "bg-white/5 text-white/40 hover:bg-white/10"
                  }`}
                  data-testid={`team-league-tab-${sport.key}`}
                >
                  {sport.name}
                </button>
              ))}
            </div>

            {/* Selected favorites chips */}
            {favoriteTeams.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {favoriteTeams.map((t) => (
                  <span key={`${t.league}-${t.id}`} className="inline-flex items-center gap-1.5 bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/30 px-2 py-1 rounded text-[10px] font-semibold">
                    {t.logo && <img src={t.logo} alt="" className="w-4 h-4 object-contain" />}
                    {t.abbreviation || t.name}
                    <button onClick={() => setFavoriteTeams((prev) => prev.filter((ft) => !(ft.id === t.id && ft.league === t.league)))} className="ml-0.5 hover:text-white">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Team grid */}
            {loadingTeams ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {[...Array(8)].map((_, i) => <div key={i} className="h-16 bg-white/5 rounded-lg animate-pulse" />)}
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-48 overflow-y-auto" data-testid="teams-selection">
                {currentTeams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => toggleTeam(team)}
                    className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center transition-colors ${
                      isTeamFavorite(team.id)
                        ? "bg-[#007AFF]/20 border-[#007AFF]/50"
                        : "bg-white/5 border-white/10 hover:border-white/20"
                    }`}
                    data-testid={`team-toggle-${team.id}`}
                  >
                    {team.logo ? (
                      <img src={team.logo} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <div className="w-8 h-8 rounded bg-white/10 flex items-center justify-center text-[10px] font-bold">{team.abbreviation?.slice(0,2)}</div>
                    )}
                    <span className="text-[10px] text-white/70 truncate w-full">{team.abbreviation}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-xs tracking-wider uppercase font-semibold text-white/50 hover:text-white transition-colors" data-testid="cancel-preferences">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-[#007AFF] text-white text-xs tracking-wider uppercase font-semibold rounded-lg hover:bg-[#007AFF]/80 transition-colors disabled:opacity-50"
            data-testid="save-preferences"
          >
            {saving ? "Saving..." : "Save Preferences"}
          </button>
        </div>
      </div>
    </div>
  );
}
